import { useCallback } from 'react';
import { useGetDataHook, useSaveDataHook } from '@bymason/api-hooks-base';
import {
  artifactsClient,
  CreateArtifactDeploymentOptions,
  CreateArtifactOptions,
  GetArtifactsOptions,
  GetArtifactsOptionsList,
  GetConfigArtifactOptions,
} from '@bymason/controller-api';

export {
  ArtifactType,
  ArtifactModel,
  GetArtifactsOptions,
  GetArtifactsOptionsList,
  ArtifactDeployment,
  ConfigModel,
} from '@bymason/controller-api';

/**
 * React hook that asynchronously gets list of artifacts.
 * @returns HookArray
 * @HookArray [0] getArtifacts method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useGetArtifacts(clientToken: string) {
  const handleGetData = useCallback(
    (options: GetArtifactsOptions) => {
      return artifactsClient.getArtifacts(options, clientToken);
    },
    [clientToken]
  );

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously gets a project config artifact version.
 * @returns HookArray
 * @HookArray [0] getArtifacts method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useGetConfigArtifact(clientToken: string) {
  const handleGetData = useCallback(
    (options: GetConfigArtifactOptions) => {
      return artifactsClient.getConfigArtifact(options, clientToken);
    },
    [clientToken]
  );

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously gets a list of artifact versions.
 * @returns HookArray
 * @HookArray [0] getArtifacts method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useGetArtifactListByVersionList(clientToken: string) {
  const handleGetData = useCallback(
    (optionsList: GetArtifactsOptionsList) => {
      return artifactsClient.getArtifactsByVersionList(optionsList, clientToken);
    },
    [clientToken]
  );

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously gets a list of artifact versions.
 * @returns HookArray
 * @HookArray [0] createArtifact method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useCreateArtifactDeployment(clientToken: string) {
  const handleGetData = useCallback(
    (options: CreateArtifactDeploymentOptions) => {
      return artifactsClient.createArtifactDeployment(options, clientToken);
    },
    [clientToken]
  );

  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously uploads a file as a new artifact.
 * @returns HookArray
 * @HookArray [0] upload method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useCreateArtifact(clientToken: string) {
  const handle = useCallback(
    (options: CreateArtifactOptions) => {
      return artifactsClient.createArtifact(options, clientToken);
    },
    [clientToken]
  );
  return useSaveDataHook(handle);
}
